// @flow

import * as React from 'react';
import { compose, withState, withHandlers } from 'recompose';
import { connect } from 'react-redux';
import Radium from 'radium';
import uuid from 'uuid';
import {
  Card,
  CardHeader,
  CardBlock,
  FormGroup,
  Label,
  Button,
  Input,
  Alert,
} from 'reactstrap';
import firebase from 'firebase/app';

import ImageUpload from 'components/ImageUpload';
import OptionsSelector from 'components/OptionsSelector';
import { Spacing } from 'utils/styleguide';

type Props = {
  // recompose
  title: string,
  onTitleChange: (title: string) => void,
  description: string,
  onDescriptionChange: (description: string) => void,
  images: { [imageId: string]: string },
  onImagesChange: (images: { [imageId: string]: string }) => void,
  onImageFilesChange: (images: { [imageId: string]: File }) => void,
  setImage: (id: string, data: string) => void,
  imageFiles: { [imageId: string]: File },
  setImageFile: (id: string, file: File) => void,
  assignees: Array<string>,
  onAssigneesChange: (assignees: Array<string>) => void,
  address: string,
  onAddressChange: (address: string) => void,

  // redux
  me: Object,
  areEmployeesLoading: boolean,
  error: ?String,
};

type State = {
  employees: Object,
};

class NewAssignment extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    if (!!props.me) {
      this._createSnapshotListener(props.me);
    }
  }
  state = {
    employees: {},
  };

  componentWillReceiveProps(next) {
    if (!this.props.me && next.me && next.me.uid) {
      const { me } = next;
      this._createSnapshotListener(me);
    }
  }

  _createSnapshotListener = me => {
    firebase
      .firestore()
      .collection(`users`)
      .where('communityId', '==', me.communityId)
      .where('roles', 'array-contains', 'employee')
      .onSnapshot(snap => {
        const employees = snap.docs
          .map(doc => ({ ...doc.data(), uid: doc.id }))
          .filter(_user => _user.uid !== me.uid)
          .reduce((acc, curr) => ({ ...acc, [curr.uid]: curr }), {});

        this.setState(prevState => ({ ...prevState, employees }));
      });
  };

  _onSubmitAssignment = async () => {
    const {
      me,
      title,
      description,
      imageFiles,
      onImageFilesChange,
      assignees,
      address,
      onTitleChange,
      onDescriptionChange,
      onAssigneesChange,
      onImagesChange,
      onAddressChange,
    } = this.props;
    const { employees } = this.state;

    console.log({ imageFiles });

    await Promise.all(
      Object.keys(imageFiles).map(async filename => {
        await firebase
          .storage()
          .ref(filename)
          .put(imageFiles[filename]);
      })
    );

    await firebase
      .firestore()
      .collection('assignments')
      .add({
        title,
        description,
        images: Object.keys(imageFiles),
        assigneeUids: assignees,
        assignees: assignees.reduce(
          (acc, uid) => ({ ...acc, [uid]: employees[uid] }),
          {}
        ),
        createdAt: firebase.firestore.Timestamp.now(),
        createdBy: me.uid,
        communityId: me.communityId,
        states: [
          {
            type: 'state',
            text: 'new',
            createdBy: me,
            createdAt: firebase.firestore.Timestamp.now(),
          },
        ],
        address,
      });

    onTitleChange('');
    onDescriptionChange('');
    onAssigneesChange([]);
    onImagesChange({});
    onImageFilesChange({});
    onAddressChange('');
  };

  render() {
    const {
      areEmployeesLoading,
      title,
      onTitleChange,
      description,
      onDescriptionChange,
      onAssigneesChange,
      address,
      onAddressChange,
      images,
      setImage,
      setImageFile,
      error,
    } = this.props;
    const { employees } = this.state;

    return (
      <div>
        <Card style={styles.root}>
          <CardHeader style={styles.title}>Neue Aufgabe erstellen</CardHeader>
          <CardBlock>
            <FormGroup>
              <Input
                type="textfield"
                placeholder="Titel der Aufgabe ..."
                value={title}
                onChange={event => onTitleChange(event.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Input
                type="textarea"
                placeholder="Beschreibung der Aufgabe ..."
                value={description}
                onChange={event => onDescriptionChange(event.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Input
                type="textfield"
                placeholder="Adresse ..."
                value={address}
                onChange={event => onAddressChange(event.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label>Bilder hinzufügen</Label>
              <div style={styles.images}>
                {Object.keys(images).map(imageId => {
                  return (
                    <div key={imageId} style={{ marginRight: Spacing.Micro }}>
                      <ImageUpload
                        image={images[imageId]}
                        onImageSelected={(data, file) => {
                          setImage(imageId, data);
                          setImageFile(imageId, file);
                        }}
                      />
                    </div>
                  );
                })}
                {Object.keys(images).length < 4 && (
                  <div style={{ marginRight: Spacing.Micro }}>
                    <ImageUpload
                      onImageSelected={(data, file) => {
                        const imageId = uuid.v4();
                        setImage(imageId, data);
                        setImageFile(imageId, file);
                      }}
                    />
                  </div>
                )}
              </div>
            </FormGroup>
            {areEmployeesLoading ? (
              <div>Mitarbeiter werden geladen ...</div>
            ) : (
              <OptionsSelector
                label="Zuweisen an"
                options={Object.keys(employees).reduce(
                  (acc, uid) => ({
                    ...acc,
                    [uid]: employees[uid].displayName,
                  }),
                  {}
                )}
                onOptionsSelected={onAssigneesChange}
              />
            )}
            {!!error && (
              <Alert color="danger">
                {error}: Konnte Aufgabe nicht anlegen. Bitte Support
                kontaktieren.
              </Alert>
            )}
            <Button
              color="primary"
              disabled={!title || !description}
              onClick={this._onSubmitAssignment}
            >
              Meldung erstellen
            </Button>
          </CardBlock>
        </Card>
      </div>
    );
  }
}

const styles = {
  root: {
    width: '100%',
  },
  title: {
    fontWeight: 'bold',
  },
  images: {
    display: 'flex',
  },
};

const mapStateToProps = ({ employees: { loading }, auth }) => {
  const me = auth.firebaseUser;

  return {
    me,
    areEmployeesLoading: loading,
  };
};
const mapDispatchToProps = {};

const NewAssignmentContainer = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withState('title', 'onTitleChange', ''),
  withState('description', 'onDescriptionChange', ''),
  withState('assignees', 'onAssigneesChange', []),
  withState('images', 'onImagesChange', {}),
  withState('imageFiles', 'onImageFilesChange', {}),
  withState('address', 'onAddressChange', ''),
  withHandlers({
    setImage: ({ images, onImagesChange }) => (id: string, data: string) => {
      if (!!data) {
        onImagesChange({ ...images, [id]: data });
      } else {
        const newImages = { ...images };
        delete newImages[id];
        onImagesChange(newImages);
      }
    },
    setImageFile: ({ imageFiles, onImageFilesChange }) => (
      id: String,
      file: File
    ) => {
      if (!!file) {
        onImageFilesChange({ ...imageFiles, [id]: file });
      } else {
        const newImageFiles = { ...imageFiles };
        delete newImageFiles[id];
        onImagesChange(newImageFiles);
      }
    },
  }),
  Radium
)(NewAssignment);

export default NewAssignmentContainer;



// WEBPACK FOOTER //
// ./src/views/Assignments/NewAssignment.js